import dayjs from "dayjs";
import { DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { T } from "../../util/t";
import { Close } from "@mui/icons-material";
import { SearchForm } from "../../search/search";
import { useTakeoverDialogStyles } from "../forms/SearchTakeoverDialog";
import { AthleteButton, VisualListButton } from "./AthleteSummary";
import { useSimpleListFilter } from "../../../hooks/useListFilter";
import { AddIcon } from "./AddAthleteButton";
import { makeStyles } from "../../providers/makeStyles";
import { sortByName } from "utils/sort";
import { MembershipRequiredMessage, MembershipRequiredNotification } from "./MembershipRequired";
import { MembershipStatus } from "./MembershipStatus";

export const useListStyles = makeStyles(theme => ({
    list: {
        margin: 0,
        listStyleType: "none",
        padding: theme.spacing(0, 0, 2, 0),
        "& li": {
            marginBottom: theme.spacing(.5)
        }
    }
}));
const getAthleteName = item => item.athlete.name;

export const SelectAthleteDialog = ({ athletes, options, formAthletes, registrationOptions, exclusiveSeries, organisation, selectAthlete, appendAthlete, closeDialog, dialogId, isSeriesRegistration }) => {
    const onAddAthlete = athlete => {
        appendAthlete(athlete);
        closeDialog();
    };
    const onSelectAthlete = athlete => {
        selectAthlete(athlete);
        closeDialog();
    };

    const [eligible, ineligible] = [...athletes, ...formAthletes.reduce((members, entry) => {
        entry.team_members?.forEach(member => !member.athlete_id && !members.find(m => m.name === member.name) && members.push({ ...member, registrations_attributes: [] }));
        return members;
    }, [])].sort(sortByName).reduce(([eligible, ineligible], athlete) => {
        if (athlete.registrations_attributes?.[0]?.team_members) return [eligible, ineligible];

        const alreadyAdded = formAthletes.findIndex(formAthlete => formAthlete.id ? formAthlete.id === athlete.id : formAthlete.name === athlete.name) > -1;
        const notMember = athlete.exclusive_memberships?.length > 0 && athlete.exclusive_memberships.some(membership => !membership.is_member);
        const notEligible = notMember || (athlete.dob && options?.every(option => {
            const optionData = registrationOptions?.divisions?.find(d => d.id === option.optionId);
            return ((optionData?.minBy || optionData?.maxBy) &&
                ((optionData.minBy && athlete.dob && dayjs(optionData.minBy).diff(athlete.dob, "year") < optionData.minAge) ||
                (optionData.maxBy && athlete.dob && dayjs(optionData.maxBy).diff(athlete.dob, "year") > optionData.maxAge))
            );
        }));

        if (alreadyAdded || notEligible)
            ineligible.push({
                athlete,
                subtext: alreadyAdded
                    ? <T>Already added</T>
                    : notMember
                        ? athlete.exclusive_memberships.map(membership => <MembershipStatus key={membership.series.id} membership={membership} />)
                        : <T>Outside age limit</T>,
                onSelect: alreadyAdded && onSelectAthlete,
                disabled: !!notEligible
            });
        else
            eligible.push({
                athlete,
                subtext: athlete.subtext,
                onSelect: onAddAthlete
            });
        return [eligible, ineligible];
    }, [[], []]);

    const {
        inputRef,
        searchString,
        onSearch,
        filteredList
    } = useSimpleListFilter(eligible.concat(ineligible), getAthleteName);

    const classes = useTakeoverDialogStyles();
    const listClasses = useListStyles();

    return (
        <>
            <DialogTitle id={dialogId}>
                <Typography variant="h3" component="div" className={classes.title}>
                    <T>{isSeriesRegistration ? "Select athlete" : "New entry"}</T>
                    <IconButton color="inherit" size="small" onClick={closeDialog}><Close/></IconButton>
                </Typography>

                {exclusiveSeries.length > 0 && athletes.some(athlete => athlete.exclusive_memberships.some(membership => !membership.is_member)) &&
                <Typography component="div" marginBottom={2}>
                    <MembershipRequiredNotification exclusiveSeries={exclusiveSeries} organisation={organisation}>
                        exclusive_registration_notification_dialog
                    </MembershipRequiredNotification>
                </Typography>}

                <Typography component="div" marginTop={2}>
                    <SearchForm inputRef={inputRef} search={searchString} onChange={onSearch}/>
                </Typography>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                <ul className={listClasses.list}>
                    {filteredList.map(item =>
                        <li key={item.athlete.id || item.athlete.name}>
                            <AthleteButton name={item.athlete.name} image={item.athlete.image} subtext={item.subtext} disabled={item.disabled} onClick={() => item.onSelect(item.athlete)}/>
                        </li>
                    )}

                    {exclusiveSeries.length === 0 && <li>
                        <VisualListButton
                            icon={<AddIcon/>}
                            text={<T>Create new athlete</T>}
                            onClick={() => onAddAthlete({ registrations_attributes: [] })}
                        />
                    </li>}
                </ul>

                {exclusiveSeries.length > 0 && <MembershipRequiredMessage exclusiveSeries={exclusiveSeries} organisation={organisation} />}
            </DialogContent>
        </>
    );
};
